import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { glorifyApiClient, GlorifyApiError } from '../../../services/api/glorify'
import type { Client, InvoiceDetail, InvoiceLineItem } from '../../../services/api/glorify'
import {
  formatAud,
  formatDate,
  formatTimestamp,
  invoiceStatusChipClass,
  invoiceStatusLabel,
  isDraft,
  legalNextStatuses,
} from './invoicingFormat'
import { toSectionError, type SectionError } from './marketsTheme'
import { SectionErrorBanner } from './marketsShared'

// ─── Numeric input parsing — never propagates NaN/blank into a request body ──

function numStr(raw: string, fallback: string): string {
  const trimmed = raw.trim()
  if (trimmed === '' || !Number.isFinite(Number(trimmed))) return fallback
  return trimmed
}

// ─── Line-item add form ─────────────────────────────────────────────────────

interface AddLineItemFormProps {
  nextSortOrder: number
  onAdd: (payload: { description: string; quantity: string; unit_price: string; gst_applicable: boolean; sort_order: number }) => Promise<void>
}

function AddLineItemForm({ nextSortOrder, onAdd }: AddLineItemFormProps): React.JSX.Element {
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [unitPrice, setUnitPrice] = useState('')
  const [gstApplicable, setGstApplicable] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const canSubmit = description.trim().length > 0 && unitPrice.trim() !== '' && !isSubmitting

  const handleAdd = async (): Promise<void> => {
    if (!canSubmit) return
    setIsSubmitting(true)
    try {
      await onAdd({
        description: description.trim(),
        quantity: numStr(quantity, '1'),
        unit_price: numStr(unitPrice, '0'),
        gst_applicable: gstApplicable,
        sort_order: nextSortOrder,
      })
      setDescription('')
      setQuantity('1')
      setUnitPrice('')
      setGstApplicable(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <tr>
      <td>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Description"
          aria-label="New line item description"
        />
      </td>
      <td>
        <input
          type="number"
          step="0.01"
          min="0"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          aria-label="New line item quantity"
        />
      </td>
      <td>
        <input
          type="number"
          step="0.01"
          min="0"
          value={unitPrice}
          onChange={(e) => setUnitPrice(e.target.value)}
          placeholder="0.00"
          aria-label="New line item unit price"
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={gstApplicable}
          onChange={(e) => setGstApplicable(e.target.checked)}
          aria-label="GST applicable"
        />
      </td>
      <td className="gd-cell-muted">—</td>
      <td>
        <button className="gd-btn gd-btn--tonal" style={{ height: 34, fontSize: 13, padding: '0 14px' }} disabled={!canSubmit} onClick={() => void handleAdd()}>
          {isSubmitting ? 'Adding…' : 'Add'}
        </button>
      </td>
    </tr>
  )
}

// ─── Line-item row (view + inline edit) ─────────────────────────────────────

interface LineItemRowProps {
  item: InvoiceLineItem
  editable: boolean
  onSave: (lineItemId: number, payload: { description: string; quantity: string; unit_price: string; gst_applicable: boolean }) => Promise<void>
  onDelete: (lineItemId: number) => Promise<void>
}

function LineItemRow({ item, editable, onSave, onDelete }: LineItemRowProps): React.JSX.Element {
  const [isEditing, setIsEditing] = useState(false)
  const [description, setDescription] = useState(item.description)
  const [quantity, setQuantity] = useState(item.quantity)
  const [unitPrice, setUnitPrice] = useState(item.unit_price)
  const [gstApplicable, setGstApplicable] = useState(item.gst_applicable)
  const [isBusy, setIsBusy] = useState(false)

  const startEdit = (): void => {
    setDescription(item.description)
    setQuantity(item.quantity)
    setUnitPrice(item.unit_price)
    setGstApplicable(item.gst_applicable)
    setIsEditing(true)
  }

  const handleSave = async (): Promise<void> => {
    setIsBusy(true)
    try {
      await onSave(item.line_item_id, {
        description: description.trim() || item.description,
        quantity: numStr(quantity, item.quantity),
        unit_price: numStr(unitPrice, item.unit_price),
        gst_applicable: gstApplicable,
      })
      setIsEditing(false)
    } finally {
      setIsBusy(false)
    }
  }

  const handleDelete = async (): Promise<void> => {
    if (!window.confirm(`Remove "${item.description}" from this invoice?`)) return
    setIsBusy(true)
    try {
      await onDelete(item.line_item_id)
    } finally {
      setIsBusy(false)
    }
  }

  if (isEditing) {
    return (
      <tr>
        <td><input type="text" value={description} onChange={(e) => setDescription(e.target.value)} aria-label="Description" /></td>
        <td><input type="number" step="0.01" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)} aria-label="Quantity" /></td>
        <td><input type="number" step="0.01" min="0" value={unitPrice} onChange={(e) => setUnitPrice(e.target.value)} aria-label="Unit price" /></td>
        <td style={{ textAlign: 'center' }}>
          <input type="checkbox" checked={gstApplicable} onChange={(e) => setGstApplicable(e.target.checked)} aria-label="GST applicable" />
        </td>
        <td>{formatAud(item.line_total)}</td>
        <td style={{ display: 'flex', gap: 4 }}>
          <button className="gd-btn gd-btn--tonal" style={{ height: 34, fontSize: 13, padding: '0 12px' }} disabled={isBusy} onClick={() => void handleSave()}>
            Save
          </button>
          <button className="gd-btn gd-btn--text" style={{ height: 34, fontSize: 13, padding: '0 8px' }} disabled={isBusy} onClick={() => setIsEditing(false)}>
            Cancel
          </button>
        </td>
      </tr>
    )
  }

  return (
    <tr>
      <td>{item.description}</td>
      <td className="gd-cell-muted">{item.quantity}</td>
      <td className="gd-cell-muted">{formatAud(item.unit_price)}</td>
      <td style={{ textAlign: 'center' }} className="gd-cell-muted">{item.gst_applicable ? 'Yes' : 'No'}</td>
      <td><b>{formatAud(item.line_total)}</b></td>
      <td>
        {editable && (
          <div style={{ display: 'flex', gap: 2 }}>
            <button className="gd-btn gd-btn--icon" style={{ width: 34, height: 34 }} disabled={isBusy} onClick={startEdit} aria-label="Edit line item" title="Edit">
              <span className="material-icons" aria-hidden="true">edit</span>
            </button>
            <button className="gd-btn gd-btn--icon" style={{ width: 34, height: 34 }} disabled={isBusy} onClick={() => void handleDelete()} aria-label="Delete line item" title="Delete">
              <span className="material-icons" aria-hidden="true">delete</span>
            </button>
          </div>
        )}
      </td>
    </tr>
  )
}

// ─── Header edit panel (draft only) ─────────────────────────────────────────

interface HeaderEditPanelProps {
  invoice: InvoiceDetail
  clients: Client[]
  onSave: (payload: { client_id?: number; issue_date?: string; due_date?: string; notes?: string }) => Promise<void>
  onCancel: () => void
}

function HeaderEditPanel({ invoice, clients, onSave, onCancel }: HeaderEditPanelProps): React.JSX.Element {
  const [clientId, setClientId] = useState(String(invoice.client.client_id))
  const [issueDate, setIssueDate] = useState(invoice.issue_date)
  const [dueDate, setDueDate] = useState(invoice.due_date)
  const [notes, setNotes] = useState(invoice.notes ?? '')
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async (): Promise<void> => {
    setIsSaving(true)
    try {
      await onSave({
        client_id: Number(clientId),
        issue_date: issueDate,
        due_date: dueDate,
        notes,
      })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="gd-card gd-card--outlined" style={{ marginBottom: 16 }}>
      <div className="gd-setup-row">
        <div className="gd-field">
          <label htmlFor="gd-edit-client">Client</label>
          <select id="gd-edit-client" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            {clients.map((c) => (
              <option key={c.client_id} value={c.client_id}>{c.client_name}</option>
            ))}
          </select>
        </div>
        <div className="gd-field">
          <label htmlFor="gd-edit-issue-date">Issue date</label>
          <input id="gd-edit-issue-date" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
        </div>
        <div className="gd-field">
          <label htmlFor="gd-edit-due-date">Due date</label>
          <input id="gd-edit-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </div>
        <div className="gd-field" style={{ flex: 1, minWidth: 220 }}>
          <label htmlFor="gd-edit-notes">Notes</label>
          <input id="gd-edit-notes" type="text" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="gd-btn gd-btn--filled" disabled={isSaving} onClick={() => void handleSave()}>
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
        <button className="gd-btn gd-btn--text" disabled={isSaving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function InvoiceDetailPage(): React.JSX.Element {
  const { invoiceId: invoiceIdParam } = useParams<{ invoiceId: string }>()
  const invoiceId = Number(invoiceIdParam)
  const navigate = useNavigate()

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null)
  const [clients, setClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<SectionError | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [headerEditing, setHeaderEditing] = useState(false)
  const [nextStatusId, setNextStatusId] = useState<string>('')
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const fetchInvoice = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const detail = await glorifyApiClient.getInvoice(invoiceId)
      setInvoice(detail)
    } catch (err) {
      setLoadError(toSectionError(err, 'Failed to load invoice'))
    } finally {
      setIsLoading(false)
    }
  }, [invoiceId])

  const fetchClients = useCallback(async (): Promise<void> => {
    try {
      const resp = await glorifyApiClient.listClients()
      setClients(resp.clients)
    } catch {
      // Non-fatal — only needed for the header-edit client select.
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchInvoice()
    void fetchClients()
  }, [fetchInvoice, fetchClients])

  // Auto-dismiss action error after 5s
  useEffect(() => {
    if (!actionError) return
    const timer = setTimeout(() => setActionError(null), 5000)
    return () => clearTimeout(timer)
  }, [actionError])

  const draft = invoice ? isDraft(invoice.invoice_status_id) : false

  const handleHeaderSave = async (payload: { client_id?: number; issue_date?: string; due_date?: string; notes?: string }): Promise<void> => {
    try {
      const updated = await glorifyApiClient.updateInvoiceHeader(invoiceId, payload)
      setInvoice(updated)
      setHeaderEditing(false)
    } catch (err) {
      setActionError(err instanceof GlorifyApiError ? err.message : 'Failed to update invoice details')
    }
  }

  const handleAddLineItem = async (payload: { description: string; quantity: string; unit_price: string; gst_applicable: boolean; sort_order: number }): Promise<void> => {
    try {
      const updated = await glorifyApiClient.addLineItem(invoiceId, payload)
      setInvoice(updated)
    } catch (err) {
      setActionError(err instanceof GlorifyApiError ? err.message : 'Failed to add line item')
    }
  }

  const handleSaveLineItem = async (lineItemId: number, payload: { description: string; quantity: string; unit_price: string; gst_applicable: boolean }): Promise<void> => {
    try {
      const updated = await glorifyApiClient.updateLineItem(invoiceId, lineItemId, payload)
      setInvoice(updated)
    } catch (err) {
      setActionError(err instanceof GlorifyApiError ? err.message : 'Failed to update line item')
    }
  }

  const handleDeleteLineItem = async (lineItemId: number): Promise<void> => {
    try {
      const updated = await glorifyApiClient.deleteLineItem(invoiceId, lineItemId)
      setInvoice(updated)
    } catch (err) {
      setActionError(err instanceof GlorifyApiError ? err.message : 'Failed to delete line item')
    }
  }

  const handleUpdateStatus = async (): Promise<void> => {
    if (!nextStatusId) return
    setIsUpdatingStatus(true)
    try {
      const updated = await glorifyApiClient.updateInvoiceStatus(invoiceId, Number(nextStatusId))
      setInvoice(updated)
      setNextStatusId('')
    } catch (err) {
      setActionError(err instanceof GlorifyApiError ? err.message : 'Failed to update invoice status')
    } finally {
      setIsUpdatingStatus(false)
    }
  }

  const handleDownloadPdf = async (): Promise<void> => {
    if (!invoice) return
    setIsDownloading(true)
    try {
      await glorifyApiClient.downloadInvoicePdf(invoice.invoice_id, invoice.invoice_number)
    } catch (err) {
      setActionError(err instanceof GlorifyApiError ? err.message : 'Failed to download PDF')
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDelete = async (): Promise<void> => {
    if (!invoice) return
    if (!window.confirm(`Permanently delete draft invoice ${invoice.invoice_number}? This cannot be undone.`)) return
    setIsDeleting(true)
    try {
      await glorifyApiClient.deleteInvoice(invoice.invoice_id)
      navigate('/dashboard/invoicing')
    } catch (err) {
      setActionError(err instanceof GlorifyApiError ? err.message : 'Failed to delete invoice')
      setIsDeleting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="gd-empty">
        <div className="gd-spinner" aria-label="Loading invoice" />
      </div>
    )
  }

  if (!invoice) {
    return (
      <>
        <SectionErrorBanner error={loadError} />
        <div className="gd-empty">
          <span className="material-icons" aria-hidden="true">receipt_long</span>
          <p>Invoice not found.</p>
        </div>
      </>
    )
  }

  const nextStatuses = legalNextStatuses(invoice.invoice_status_id)
  const nextSortOrder = invoice.line_items.length

  return (
    <>
      <div className="gd-page-header gd-page-header--with-action">
        <div className="gd-page-header__text">
          <h1>{invoice.invoice_number}</h1>
          <p>{invoice.client.client_name}</p>
        </div>
        <span className={invoiceStatusChipClass(invoice.invoice_status_id)}>
          {invoiceStatusLabel(invoice.invoice_status_id)}
        </span>
      </div>

      {actionError && (
        <div className="gd-action-error" role="alert">
          <span className="material-icons" aria-hidden="true">error_outline</span>
          {actionError}
        </div>
      )}
      <SectionErrorBanner error={loadError} />

      {/* ── Header details ───────────────────────────────────────────── */}
      <div className="gd-section">
        <div className="gd-section__head">
          <h2 className="gd-section__title" style={{ marginBottom: 0 }}>Details</h2>
          {draft && !headerEditing && (
            <button className="gd-btn gd-btn--outlined" onClick={() => setHeaderEditing(true)}>
              Edit details
            </button>
          )}
        </div>

        {!draft && (
          <div className="gd-banner gd-banner--info" style={{ marginBottom: 16 }}>
            <span className="material-icons" aria-hidden="true">info</span>
            <p>
              This invoice is {invoiceStatusLabel(invoice.invoice_status_id).toLowerCase()} — header and line-item
              editing is only available while an invoice is in Draft, so the generated PDF stays representative of
              what was actually sent.
            </p>
          </div>
        )}

        {headerEditing ? (
          <HeaderEditPanel
            invoice={invoice}
            clients={clients}
            onSave={handleHeaderSave}
            onCancel={() => setHeaderEditing(false)}
          />
        ) : (
          <div className="gd-quote-grid">
            <div>
              <div className="gd-quote-stat__label">Client</div>
              <div className="gd-quote-stat__value">{invoice.client.client_name}</div>
            </div>
            <div>
              <div className="gd-quote-stat__label">Issue date</div>
              <div className="gd-quote-stat__value">{formatDate(invoice.issue_date)}</div>
            </div>
            <div>
              <div className="gd-quote-stat__label">Due date</div>
              <div className="gd-quote-stat__value">{formatDate(invoice.due_date)}</div>
            </div>
            <div>
              <div className="gd-quote-stat__label">Notes</div>
              <div className="gd-quote-stat__value">{invoice.notes || '—'}</div>
            </div>
            {invoice.paid_at && (
              <div>
                <div className="gd-quote-stat__label">Paid at</div>
                <div className="gd-quote-stat__value">{formatTimestamp(invoice.paid_at)}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Line items ────────────────────────────────────────────────── */}
      <div className="gd-section">
        <h2 className="gd-section__title">Line Items</h2>
        <div className="gd-table-wrap">
          <table className="gd-table gd-legs-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th style={{ textAlign: 'center' }}>GST</th>
                <th>Line Total</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {invoice.line_items.length === 0 && !draft && (
                <tr>
                  <td colSpan={6} className="gd-cell-muted">No line items on this invoice.</td>
                </tr>
              )}
              {invoice.line_items.map((item) => (
                <LineItemRow
                  key={item.line_item_id}
                  item={item}
                  editable={draft}
                  onSave={handleSaveLineItem}
                  onDelete={handleDeleteLineItem}
                />
              ))}
              {draft && <AddLineItemForm nextSortOrder={nextSortOrder} onAdd={handleAddLineItem} />}
            </tbody>
          </table>
        </div>

        <div className="gd-summary-row" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
          <div className="gd-card gd-card--elevated" style={{ minWidth: 160 }}>
            <div className="gd-summary-card__label">Subtotal</div>
            <div className="gd-summary-card__value">{formatAud(invoice.subtotal_amount)}</div>
          </div>
          <div className="gd-card gd-card--elevated" style={{ minWidth: 160 }}>
            <div className="gd-summary-card__label">GST</div>
            <div className="gd-summary-card__value">{formatAud(invoice.gst_amount)}</div>
          </div>
          <div className="gd-card gd-card--elevated" style={{ minWidth: 160 }}>
            <div className="gd-summary-card__label">Total</div>
            <div className="gd-summary-card__value">{formatAud(invoice.total_amount)}</div>
          </div>
          <div className="gd-card gd-card--elevated" style={{ minWidth: 160 }}>
            <div className="gd-summary-card__label">Amount Paid</div>
            <div className="gd-summary-card__value">{formatAud(invoice.amount_paid)}</div>
          </div>
        </div>
      </div>

      {/* ── Actions ───────────────────────────────────────────────────── */}
      <div className="gd-section">
        <h2 className="gd-section__title">Actions</h2>
        <div className="gd-setup-row" style={{ alignItems: 'center' }}>
          {nextStatuses.length > 0 && (
            <>
              <div className="gd-field">
                <label htmlFor="gd-next-status">Change status to</label>
                <select id="gd-next-status" value={nextStatusId} onChange={(e) => setNextStatusId(e.target.value)}>
                  <option value="">Select…</option>
                  {nextStatuses.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <button
                className="gd-btn gd-btn--filled"
                disabled={!nextStatusId || isUpdatingStatus}
                onClick={() => void handleUpdateStatus()}
              >
                {isUpdatingStatus ? 'Updating…' : 'Update status'}
              </button>
            </>
          )}

          <button className="gd-btn gd-btn--tonal has-icon" disabled={isDownloading} onClick={() => void handleDownloadPdf()}>
            <span className="material-icons" aria-hidden="true">download</span>
            {isDownloading ? 'Downloading…' : 'Download PDF'}
          </button>

          {draft && (
            <button className="gd-btn gd-btn--outlined has-icon" style={{ color: 'var(--md-sys-color-error)', borderColor: 'var(--md-sys-color-error)' }} disabled={isDeleting} onClick={() => void handleDelete()}>
              <span className="material-icons" aria-hidden="true">delete_forever</span>
              {isDeleting ? 'Deleting…' : 'Delete draft'}
            </button>
          )}
        </div>
      </div>
    </>
  )
}
